import { z } from 'zod'

import { isoDateTime } from '../lib/date'
import { regionIdSchema } from './region'

/**
 * Core `Member` entity - mirrors the `members` DB row shape and is
 * the canonical member primitive across the app. API request/response
 * shapes and portal-safe projections (`PublicMember`) are composed
 * from this in `api/os/members.ts` and `api/portal/shapes.ts`.
 */
export const memberSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phone: z.string(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  city: z.string().nullable(),
  postalCode: z.string().nullable(),
  /** US state/territory (2-letter code, e.g. 'NJ'). Null for UK members. */
  state: z.string().nullable(),
  country: z.string().nullable(),
  region: regionIdSchema,
  stripeCustomerId: z.string().nullable(),
  internalNotes: z.string().nullable(),
  /** Whether the primary email has been verified via OTP. */
  emailVerified: z.boolean(),
  /** Whether the primary phone has been verified via OTP. */
  phoneVerified: z.boolean(),
  archivedAt: isoDateTime.nullable(),
  archivedReason: z.string().nullable(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
})

export type Member = z.output<typeof memberSchema>

/**
 * Nested address payload used by write endpoints that treat the
 * member's address atomically (e.g. `PATCH /portal/me`). Line 2 is
 * the only optional line - everything else is required. This shape
 * is deliberately separate from the flat address columns on `Member`
 * so callers can't smuggle a partial address (postcode-only, no line
 * 1, etc.) through the API.
 *
 * Trim happens at parse-time; line 2 collapses an empty string to
 * `null` so downstream writes get a clean nullable column value.
 */
export const memberAddressSchema = z.object({
  address1: z.string().min(1).trim(),
  address2: z
    .string()
    .trim()
    .nullable()
    .optional()
    .transform((v) => (v && v.length > 0 ? v : null)),
  city: z.string().min(1).trim(),
  state: z.string().length(2).nullable(),
  postalCode: z.string().min(1).trim(),
  country: z.string().min(1).trim(),
})
export type MemberAddress = z.infer<typeof memberAddressSchema>

/** True when the stored address columns are complete enough to be deliverable. */
export function hasValidAddress(member: Pick<Member, 'address1' | 'city' | 'postalCode'>): boolean {
  return !!(member.address1 && member.city && member.postalCode)
}
